<template>
  <div class="fit">
    <q-card class="column fit">
      <div style="height: 5px">
        <q-linear-progress indeterminate v-if="currentZoomScanDataLoading" />
      </div>

      <div class="row q-pt-md" style="position: relative; background: white">
        <div class="col-12">
          <q-btn
            @click="settingsDialog = !settingsDialog"
            size="sm"
            style="right: 0px; position: absolute; top: 0px"
            flat
            round
            color="black"
            icon="mdi-cog"
          ></q-btn>
        </div>
        <h6 class="q-ma-sm">
          Selected Point on Map
          <span
            style="color: gray; font-size: 12px; text-decoration: underline"
          >
            {{ currentZoom }}
            <q-tooltip>
              <div style="max-width: 400px">
                <h6 class="q-my-none">What is this?</h6>
                <p style="font-size: 12px">
                  This number is the current fidelity level that has been loaded
                  onto the map. The higher the number, the smaller the m/p
                  (metres per point), meaning more data will be loaded. If you
                  zoom into the map the fidelity will increase, increasing the
                  m/p, and if you zoom out it will decrease the m/p.<br />
                  <br />
                  We always show the worst readings between the two points, for
                  example, if you are viewing 100 m/p, and in the first 100
                  meters the highest reading was 8, we would display 8 as that
                  points reading. As you zoom in, more data is loaded in meaning
                  the real location of that 8 reading will be narrowed down. If
                  you need more precise data, you can export a survey to a CSV
                  at any point, and it will export the raw data.<br /><br />
                  The different fidelity levels are linked to the distance
                  between each point. The fidelity distances from 0-18 are as
                  follows
                  <br />
                  <br />
                  {{ fidelityLevels.join(", ") }}
                </p>
                <p style="font-size: 12px; font-weight: bold">
                  You are currently viewing {{ fidelityLevels[currentZoom] }}
                  <br />
                  Total points loaded in: {{ totalPointsLoadedIn }}
                </p>
              </div>
            </q-tooltip>
          </span>
        </h6>
        <!-- <q-btn @click="generareDebug()">Debug</q-btn> -->
        <div v-if="render" class="col-12 q-px-xs">
          <div style="position: relative">
            <svg
              style="position: absolute; top: 25%; height: 100%"
              width="100%"
              preserveAspectRatio="none"
              :viewBox="`0 0 ${currentZoomScanData[currentMapIndex].normalizedData.length} 4`"
            >
              <rect
                v-for="(path, index) in currentZoomScanData[currentMapIndex]
                  .normalizedData"
                :key="index"
                :x="index"
                y="0"
                width="1"
                height="2"
                :fill="
                  strokeColors[
                    path.value < lowerLimit
                      ? 0
                      : path.value > upperLimit
                      ? 2
                      : 1
                  ]
                "
                :fill-opacity="
                  index < geoPointRecordingRange.min ||
                  index > geoPointRecordingRange.max
                    ? '0.5'
                    : '1.0'
                "
                stroke-opacity="0"
              />

              Sorry, your browser does not support inline SVG.
            </svg>

            <q-slider
              id="reportSlider"
              color="black"
              v-model="slider"
              :min="sliderOptions.min"
              :max="sliderOptions.max"
              @input="updateCenterPOS"
            />
          </div>
        </div>
        <div v-if="render" class="col-12">
          <div class="row">
            <div
              class="col"
              v-for="(survey, index) in currentZoomScanData"
              :key="`survey_${index}`"
            >
              <div class="row">
                <div class="col-12 text-center">
                  <h6
                    class="q-ma-xs"
                    style="font-size: 12pt; line-height: 10pt"
                  >
                    {{ survey.name }}
                    <br />
                    <q-chip
                      square
                      size="xs"
                      :color="graphColors[index]"
                    ></q-chip>
                    <q-btn
                      v-if="index != 0"
                      icon="close"
                      color="primary"
                      size="sm"
                      round
                      flat
                      @click="removeSurvey(survey.id)"
                    ></q-btn>
                    <q-btn
                      icon="map"
                      :color="currentMapIndex == index ? 'primary' : 'black'"
                      size="sm"
                      round
                      flat
                      @click="changeDisplayedMap(index)"
                    ></q-btn>
                  </h6>
                  <span style="top: -10px; position: relative">
                    250mm Scan Depth
                  </span>
                </div>
              </div>
              <div
                class="row q-px-lg"
                v-if="survey.normalizedData[currentSelectedIndex]"
              >
                <div
                  class="col-12 text-center"
                  :style="`background-color: ${getColor([
                    survey.normalizedData[currentSelectedIndex].value,
                  ])}`"
                >
                  {{
                    roundTwoDP(
                      survey.normalizedData[currentSelectedIndex].value
                    )
                  }}
                </div>
              </div>
            </div>
          </div>
        </div>
        <div v-if="currentUserData.role == 'Admin'" class="col-12 q-px-sm">
          <q-btn
            label="Split Survey at Point"
            class="full-width q-mt-sm"
            color="primary"
            @click="splitDataset"
          >
          </q-btn>
        </div>
        <div class="col-12 q-px-sm">
          <q-btn
            :label="`${graph ? 'Hide Graph' : 'Show Graph'}`"
            class="full-width q-mt-sm"
            color="primary"
            @click="renderGraph"
          >
          </q-btn>
        </div>
        <!-- <h6 v-if="graph" class="q-ma-sm">Crop Graph Using Slider</h6> -->
        <!-- <div v-if="render && graph" class="col-12 q-px-xs">
          <div style="position: relative">
            <svg
              style="position: absolute; top: 25%; height: 100%; right: 6px"
              width="calc(100% - 50px)"
              preserveAspectRatio="none"
              :viewBox="`0 0 ${graphPoints[0].length} 4`"
            >
              <rect
                v-for="(path, index) in graphPoints"
                :key="index"
                :x="index"
                y="0"
                width="1"
                height="2"
                :fill="
                  strokeColors[
                    path.value < lowerLimit
                      ? 0
                      : path.value > upperLimit
                      ? 2
                      : 1
                  ]
                "
                :fill-opacity="
                  graph &&
                  (index < geoPointRecordingRange.min ||
                    index > geoPointRecordingRange.max)
                    ? '0.5'
                    : '1.0'
                "
                stroke-opacity="0"
              />

              Sorry, your browser does not support inline SVG.
            </svg>

            <q-range
              style="right: -44px; width: calc(100% - 50px)"
              v-if="graph"
              color="blue"
              @change="rangeChanged"
              v-model="geoPointRecordingRange"
              :min="sliderOptions.min"
              :max="sliderOptions.max"
            />
          </div>
        </div> -->
        <div
          v-if="render && graph"
          style="
            max-height: 400px;
            height: calc(100vh - 700px);
            min-height: 200px;
            position: relative;
            overflow-y: overlay;
          "
          class="col-12"
        >
          <pwaGraphComponent
            ref="chart"
            :data="graphPoints"
            :rawData="currentZoomScanData[currentMapIndex].normalizedData"
            :upperLimit="upperLimit"
            :lowerLimit="lowerLimit"
            @updateCenterPOS="updateCenterPOS"
          ></pwaGraphComponent>
        </div>
      </div>
      <q-dialog v-model="settingsDialog">
        <q-card tile>
          <q-card-section class="q-pa-sm q-pl-md">
            <div class="row items-center">
              <div style="display: inline-block" class="text-h6">
                Moisture Limit Settings
              </div>
            </div>
          </q-card-section>
          <q-separator />
          <q-card-section>
            <div class="row">
              <div class="col-12 text-center">Adjust moisture limits</div>
              <div class="col-12">
                <q-input
                  label="Concerning Moisture Limit (Red)"
                  square
                  standout
                  type="number"
                  suffix="TDRI Index"
                  v-model.number="upperLimit"
                  :rules="[(v) => !!v || 'Upper limit is required']"
                ></q-input>
              </div>
              <div class="col-12">
                <q-input
                  label="Ideal Moisture Limit (Yellow)"
                  square
                  standout
                  type="number"
                  suffix="TDRI Index"
                  :rules="[(v) => !!v || 'Lower limit is required']"
                  v-model.number="lowerLimit"
                ></q-input>
              </div>
              <!-- <div class="col-12 text-center">
                Ad
              </div> -->
            </div>
          </q-card-section>
          <q-card-section>
            <div class="row">
              <div class="col-12">
                <q-btn
                  @click="updateLowerAndUpperLimit()"
                  class="full-width"
                  color="positive"
                  label="Confirm"
                >
                </q-btn>
              </div>
            </div>
          </q-card-section>
        </q-card>
      </q-dialog>
      <q-dialog v-model="addNewScanDialog">
        <q-card tile>
          <q-card-section class="q-pa-sm q-pl-md">
            <div class="row items-center">
              <div style="display: inline-block" class="text-h6">
                Please select a survey
              </div>
              <q-space />
              <q-btn
                color="black"
                round
                flat
                icon="mdi-close"
                @click="addNewScanDialog = false"
              >
              </q-btn>
            </div>
          </q-card-section>
          <q-separator />
          <q-card-section>
            <div class="row">
              <div class="col-12 text-center">
                <nearestLocationSelector
                  @selectSurvey="addSurveyToReport"
                  :selectedSurveys="currentZoomScanData"
                />
              </div>
            </div>
          </q-card-section>
        </q-card>
      </q-dialog>
      <q-dialog v-model="debugViewDialog">
        <q-card tile>
          <q-card-section class="q-pa-sm q-pl-md">
            <div class="row items-center">
              <div style="display: inline-block" class="text-h6">Debug</div>
              <q-space />
              <q-btn
                color="black"
                round
                flat
                icon="mdi-close"
                @click="addNewScanDialog = false"
              >
              </q-btn>
            </div>
          </q-card-section>
          <q-separator />
          <q-card-section>
            <div
              class="row"
              style="width: 500px"
              v-if="distanceFromPreviousPoint"
            >
              <div class="col-12 text-center" style="height: 600px">
                <!-- <nearestLocationSelector
                  @selectSurvey="addSurveyToReport"
                  :selectedSurveys="currentZoomScanData"
                /> -->
                <datatable
                  :filter="''"
                  :flat="true"
                  :data="distanceFromPreviousPoint"
                  :columns="columns"
                />
              </div>
            </div>
          </q-card-section>
        </q-card>
      </q-dialog>
    </q-card>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import pwaGraphComponent from "@/components/surveyReport/pwaGraphComponent.vue";
import nearestLocationSelector from "@/components/scheduledSurveys/nearestLocationSelector.vue";
import datatable from "@/components/datatable/datatable.vue";

export default {
  components: { pwaGraphComponent, nearestLocationSelector, datatable },
  computed: {
    ...mapGetters([
      "currentUser",
      "currentUserData",
      "currentZoomScanData",
      "currentZoomScanDataLoading",
      "currentMapIndex",
      "nearestLocations",
      "nearestLocationsLoading",
      "currentSelectedIndex",
      "currentSelectedIndexRelative",
      "currentZoom",
      "currentViewedReports",
      "survey",
      "currentViewedReportsToBeFlipped",
    ]),
    totalPointsLoadedIn() {
      let points = 0;
      this.currentZoomScanData.forEach((element) => {
        points += element.normalizedData.length;
      });
      return new Intl.NumberFormat("en-IN", {
        maximumSignificantDigits: 5,
      }).format(points);
    },
  },
  props: {
    id: [Number, String],
  },
  data: () => ({
    multiple: false,
    settingsDialog: false,
    // geoPointRecording: 0,
    slider: 0,
    geoPointRecordingRange: {
      max: 0,
      min: 0,
    },
    sliderOptions: {
      min: 0,
      max: 1,
    },
    render: false,
    upperLimit: 12,
    lowerLimit: 6,
    overlayAllData: false,
    selectedColor: "white",

    // options: ,

    strokeColors: ["#008000", "#eb9c34", "#eb4034"],

    // Multiple Scans
    addNewScanDialog: false,
    // currentMapIndex: 0,

    debugViewDialog: false,
    distanceFromPreviousPoint: [],
    columns: [
      {
        name: "index",
        label: "Index",
        align: "left",
        field: "index",
        sortable: true,
      },
      {
        name: "distance",
        label: "Distance",
        align: "left",
        field: "distance",
        sortable: true,
      },
      {
        name: "value",
        label: "Value",
        align: "left",
        field: "value",
        sortable: true,
      },
    ],

    updateCenterDebounce: null,

    // Graphing
    graphPoints: [],
    graphColors: ["green-10", "pink-6", "indigo-10"],
    graph: false,
    graphGranularity: 0,

    // Fidelity m/p
    fidelityLevels: [
      "200,000 m/p",
      "100,000 m/p",
      "50,000 m/p",
      "30,000 m/p",
      "15,000 m/p",
      "7,000 m/p",
      "4,000 m/p",
      "2,000 m/p",
      "1,000 m/p",
      "500 m/p",
      "200 m/p",
      "100 m/p",
      "50 m/p",
      "30 m/p",
      "10 m/p",
      "5 m/p",
      "2 m/p",
      "1 m/p",
      "0.5 m/p",
    ],

    // Split Survey
    splitSurveyLoading: false,
  }),
  beforeCreate() {},
  created() {
    window.addEventListener("resize", this.windowChanged);
  },
  destroyed() {
    window.removeEventListener("resize", this.windowChanged);
  },
  mounted() {
    this.slider = this.currentSelectedIndex;
  },
  watch: {
    currentZoomScanData() {
      if (this.render == false) {
        this.init();
      } else {
        this.updateNormalizedData();
        if (this.graph) {
          this.updateGraph();
        }
      }
    },
    currentSelectedIndex() {
      this.slider = this.currentSelectedIndex;
      this.calculateColor();
      if (this.graph) {
        let relativePosition =
          this.currentSelectedIndex /
          this.currentZoomScanData[this.currentMapIndex].normalizedData.length;

        let index = Math.round(this.graphPoints[0].length * relativePosition);

        this.$refs.chart.dataPointSelection(index);
      }
    },
    survey: function () {
      let ids = this.survey.mergedScans
        ? this.survey.mergedScans.map((scan) => scan.merged_id)
        : [this.survey.id];
      if (
        !this.currentViewedReports ||
        !this.currentViewedReports.includes(ids[0])
      ) {
        this.lowerLimit = this.survey.lowerLimit;
        this.upperLimit = this.survey.upperLimit;
      }
    },
  },
  methods: {
    windowChanged() {
      this.graph = false;
    },
    init() {
      // this.$q.loading.show();
      this.graph = false;
      // this.geoPointRecording = 0;
      this.updateNormalizedData();
      this.lowerLimit =
        this.currentZoomScanData[this.currentMapIndex].lowerLimit;
      this.upperLimit =
        this.currentZoomScanData[this.currentMapIndex].upperLimit;
      setTimeout(() => {
        this.calculateColor();
        // this.updateCenterPOS(this.geoPointRecording);
        this.render = true;
      }, 200);
    },
    updateNormalizedData() {
      this.sliderOptions.max =
        this.currentZoomScanData[this.currentMapIndex].normalizedData.length -
        1;
      this.geoPointRecordingRange.max =
        this.currentZoomScanData[this.currentMapIndex].normalizedData.length -
        1;
    },
    setPosition(position) {
      this.coords.lng = position.lng;
      this.coords.lat = position.lat;
    },
    updateCenterPOS(index) {
      clearTimeout(this.updateCenterDebounce);
      this.updateCenterDebounce = setTimeout(() => {
        this.$emit(
          "panToLocation",
          this.currentZoomScanData[this.currentMapIndex].normalizedData[index],
          index
        );
        this.$store.dispatch("changeSelectedIndex", index);
      }, 25);

      // this.$refs.chart.dataPointSelection(index);
      // this.$refs.gmapReport.panToLocation(this.normalizedData[index]);
    },
    runError(error) {
      this.$q.notify({
        position: "top-right",
        type: "error",
        message: error.message,
      });
    },
    calculateColor() {
      let color =
        this.strokeColors[
          this.currentZoomScanData[this.currentMapIndex].normalizedData[
            this.currentSelectedIndex
          ].value < this.lowerLimit
            ? 0
            : this.currentZoomScanData[this.currentMapIndex].normalizedData[
                this.currentSelectedIndex
              ].value > this.upperLimit
            ? 2
            : 1
        ];
      this.selectedColor = color;
    },
    getColor(value) {
      let color =
        this.strokeColors[
          value < this.lowerLimit ? 0 : value > this.upperLimit ? 2 : 1
        ];
      return color;
    },
    rangeChanged() {
      let min = this.geoPointRecordingRange.min;
      let max = this.geoPointRecordingRange.max;
      this.$store.dispatch("changeSelectedIndex", Math.floor((max + min) / 2));
      setTimeout(() => {
        this.$refs.chart.zoom(min, max);
      }, 50);
      this.$emit("rangeChanged", min, max);
    },

    roundTwoDP(val) {
      return Math.round((parseFloat(val) + Number.EPSILON) * 100) / 100;
    },

    openAddNewScan() {
      this.addNewScanDialog = true;
      this.$store.dispatch("loadNearestLocations", {
        id: this.$route.params.report_id,
        distance: 0.1,
      });
    },
    addSurveyToReport(survey) {
      // this.$q.loading.show();
      let idList = this.currentViewedReports;
      let toBeFlipped = this.currentViewedReportsToBeFlipped;
      idList.push(
        survey.mergedScans && survey.mergedScans.length > 0
          ? survey.mergedScans.map((scan) => scan.merged_id)
          : [survey.id]
      );
      if (survey.toBeFlipped) {
        toBeFlipped.push(
          survey.mergedScans && survey.mergedScans.length > 0
            ? survey.mergedScans.map((scan) => scan.merged_id)
            : [survey.id]
        );
      }

      this.$store.dispatch("loadSurveyScanDataForZoom", {
        idList: idList,
        zoom: this.currentZoom,
        toBeFlipped: toBeFlipped,
      });
      this.addNewScanDialog = false;
    },
    removeSurvey(id) {
      // this.$q.loading.show();
      // this.render = false;
      if (this.currentZoomScanData[this.currentMapIndex].id == id) {
        this.changeDisplayedMap(0);
      }
      let idList = this.currentViewedReports.filter((r) => r != id);
      this.$store.dispatch("loadSurveyScanDataForZoom", {
        idList: idList,
        zoom: this.currentZoom,
      });
      // this.$store.dispatch("removeSurveyToSurveyReport", id).then(() => {
      //   this.render = true;
      // });
    },
    changeDisplayedMap(index) {
      // this.currentMapIndex = index;
      this.$store.dispatch("changeCurrentMapIndex", index);
      // this.$emit("changeDisplayedMap", index);
    },
    generareDebug() {
      let previousPoint = 0;
      for (
        let i = 1;
        i <
        this.currentZoomScanData[this.currentMapIndex].scans[0].readings.length;
        i++
      ) {
        let distance = this.getDistanceBetweenPointAPointB(
          this.currentZoomScanData[this.currentMapIndex].scans[0].readings[
            previousPoint
          ],
          this.currentZoomScanData[this.currentMapIndex].scans[0].readings[i]
        );

        if (distance > 1) {
          previousPoint = i;
          this.distanceFromPreviousPoint.push({
            index: i,
            distance: distance,
            value:
              this.currentZoomScanData[this.currentMapIndex].scans[0].readings[
                i
              ].value,
          });
        }
      }
      this.debugViewDialog = true;
    },
    getDistanceBetweenPointAPointB(a1, a2) {
      const R = 6371e3;
      const φ1 = (a1.lat * Math.PI) / 180; // φ, λ in radians
      const φ2 = (a2.lat * Math.PI) / 180;
      const Δφ = ((a2.lat - a1.lat) * Math.PI) / 180;
      const Δλ = ((a2.lng - a1.lng) * Math.PI) / 180;

      const a =
        Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
        Math.cos(φ1) * Math.cos(φ2) * Math.sin(Δλ / 2) * Math.sin(Δλ / 2);
      const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

      const d = R * c; // in metres
      return d;
    },
    updateLowerAndUpperLimit() {
      // this.$refs.chart.updateLowerUperLimits();
      this.settingsDialog = !this.settingsDialog;
      this.$emit("limitsChanged", {
        lowerLimit: this.lowerLimit,
        upperLimit: this.upperLimit,
      });
    },

    // Graphing
    renderGraph() {
      if (!this.graph) {
        this.generateGraphPoints();
        this.graph = true;
      } else {
        this.graph = false;
      }
    },
    generateGraphPoints() {
      this.graphPoints = [];

      this.currentZoomScanData.forEach((scan) => {
        let scanPoints = [];
        let currentLength = scan.normalizedData.length;
        let difference = currentLength / 300;
        this.graphGranularity = difference < 1 ? 1 : Math.floor(difference);

        for (let i = 0; i < currentLength; i += this.graphGranularity) {
          let current = {
            value: 0,
          };

          for (
            let x = 0;
            x < this.graphGranularity && x + i < currentLength;
            x++
          ) {
            let point = JSON.parse(JSON.stringify(scan.normalizedData[x + i]));
            if (parseInt(point.value) > parseInt(current.value)) {
              current = point;
            }
          }

          scanPoints.push(current);
        }

        this.graphPoints.push(scanPoints);
      });
    },
    updateGraph() {
      // this.graph = false;
      this.generateGraphPoints();
      // this.graph = true;
    },

    async splitDataset() {
      this.splitSurveyLoading = true;
      try {
        let response = await this.$store.dispatch("splitSurvey", {
          point: this.survey.normalizedData[this.currentSelectedIndex],
          survey_id: this.survey.id,
        });
        if (response.Result == "Success") {
          this.$q.notify({
            position: "top-right",
            type: "positive",
            message: response.Response.Message,
          });
        } else {
          this.$q.notify({
            position: "top-right",
            type: "positive",
            message: response.Response.Message,
          });
        }
      } catch (error) {
        this.$q.notify({
          position: "top-right",
          type: "negative",
          message: "Something Went Wrong, Please try again later",
        });
      }

      this.splitSurveyLoading = false;
    },

    // toggleGraph(value) {
    //   this.$emit("toggleGraph", value);
    //   if (value) {
    //     this.rangeChanged();
    //     this.currentSelectedIndex = 0;
    //   } else {
    //     this.updateCenterPOS(this.currentSelectedIndex);
    //   }
    // },
  },
};
</script>

<style>
#reportSlider > .q-slider__track-container {
  background-color: rgba(0, 0, 0, 0) !important;
}
</style>
